import { useMeContext } from '@shared/contexts/hooks/useMeContext';
import { JobsStateJobFragment } from '@shared/contexts/pluginContexts/EmailThreadContext';
import {
  JobSummaryFragment,
  JobType,
  useGetJobsQuery,
} from '@shared/generated/graphql';
import { useJobPolling } from '@shared/hooks/useJobPolling';
import {
  getHasUnconfirmedQuoteAndOrder,
  jobFragmentsToJobsState,
} from '@shared/plugin/utils/optimisticJobs';
import { useMemo, useState } from 'react';

type FrontJobsState = {
  hasUnconfirmedQuote: boolean;
  hasUnconfirmedOrder: boolean;
  jobs: JobSummaryFragment[];
  jobTypes: Record<string, JobType>;
  storeJobType: (jobType: JobType, jobId?: string) => void;
  loading: boolean;
};

const POLL_INTERVAL_INFREQUENT_SECONDS = 30 * 1000;

type UseFrontJobsProps = { conversationId: string };
export const useFrontJobs = (p: UseFrontJobsProps): FrontJobsState => {
  const [jobTypes, setJobTypes] = useState<Record<string, JobType>>({});
  const { defaultOrgId } = useMeContext();
  const { conversationId } = p;
  const { data, startPolling, stopPolling, loading } = useGetJobsQuery({
    variables: {
      input: {
        organizationIds: defaultOrgId ? [defaultOrgId] : [],
        frontFilter: { conversationId },
        filterUnconfirmedJobs: false,
      },
    },
    skip: !conversationId,
    pollInterval: POLL_INTERVAL_INFREQUENT_SECONDS,
  });
  useJobPolling({
    data,
    startPolling,
    stopPolling,
  });

  const storeJobType = (jobType: JobType, jobId?: string) => {
    if (!jobId) return;

    setJobTypes((prev) => ({
      ...prev,
      [jobId]: jobType,
    }));
  };

  const jobs: JobsStateJobFragment[] = useMemo(() => {
    return jobFragmentsToJobsState(data);
  }, [data]);

  const { hasUnconfirmedQuote, hasUnconfirmedOrder } = useMemo(() => {
    return getHasUnconfirmedQuoteAndOrder(data);
  }, [data]);

  return {
    jobs,
    jobTypes,
    storeJobType,
    loading,
    hasUnconfirmedQuote,
    hasUnconfirmedOrder,
  };
};
